import React from 'react';
import {Image, Text, View} from '@react-pdf/renderer';
import {IntlShape} from 'react-intl';
import {companyLogoSrc} from '../../../../directApi';
import {formatQuoteDate} from '../quoteDocument';
import QuotePage from './QuotePage';
import {buildStyles} from './quotePdfStyles';
import type {QuoteDocumentData, QuoteTemplate} from '../resolver/types';

type QuoteCoverPageProps = {
	data: QuoteDocumentData;
	template: QuoteTemplate;
	intl: IntlShape;
};

/** Cover: logo (company name fallback), eyebrow, project title, client line, and the meta grid. */
const QuoteCoverPage = ({data, template, intl}: QuoteCoverPageProps): JSX.Element => {
	const styles = buildStyles(template.font);
	const brand = template.brandColor;
	const logoSrc = data.companyLogoId ? companyLogoSrc(data.companyLogoId) : null;

	const preparedLine = data.clientName
		? intl.formatMessage(
				{id: 'quote_designer.pdf.prepared'},
				{
					company: <Text style={styles.bold}>{data.companyName}</Text>,
					client: <Text style={styles.bold}>{data.clientName}</Text>,
				}
		  )
		: intl.formatMessage(
				{id: 'quote_designer.pdf.prepared_no_client'},
				{company: <Text style={styles.bold}>{data.companyName}</Text>}
		  );

	const metaCells: Array<{label: string; value: string; brandColored?: boolean}> = [
		{label: intl.formatMessage({id: 'quote_designer.cover.quote_date'}), value: formatQuoteDate(data.issuedDate, intl)},
		{label: intl.formatMessage({id: 'quote_designer.cover.expiry_date'}), value: formatQuoteDate(data.expiryDate, intl)},
		{label: intl.formatMessage({id: 'quote_designer.cover.reference'}), value: data.projectIndicator, brandColored: true},
		{label: intl.formatMessage({id: 'quote_designer.cover.version'}), value: `v${data.revision}`},
	];

	return (
		<QuotePage data={data} template={template} intl={intl} variant="cover">
			<View style={styles.coverContent}>
				<View>
					{logoSrc ? (
						<Image src={{uri: logoSrc, credentials: 'include'}} style={styles.coverLogoImage} />
					) : (
						<Text style={styles.coverLogoWord}>{data.companyName}</Text>
					)}
				</View>
				<View>
					<Text style={[styles.coverEyebrow, {color: brand}]}>
						{intl.formatMessage({id: 'quote_designer.pdf.eyebrow'})}
					</Text>
					<Text style={styles.coverTitle}>{data.projectName}</Text>
					{data.clientName ? (
						<Text style={styles.coverFor}>
							{intl.formatMessage({id: 'quote_designer.pdf.for_client'}, {client: data.clientName})}
						</Text>
					) : null}
					<Text style={styles.coverPrepared}>{preparedLine}</Text>
				</View>
				<View style={styles.coverMeta}>
					{metaCells.map(cell => (
						<View key={cell.label} style={styles.coverMetaCell}>
							<Text style={styles.coverMetaLabel}>{cell.label}</Text>
							<Text style={[styles.coverMetaValue, cell.brandColored ? {color: brand} : {}]}>{cell.value}</Text>
						</View>
					))}
				</View>
			</View>
		</QuotePage>
	);
};

export default QuoteCoverPage;
